import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserCircle,
  UserSearch,
  Crown,
  TrendingUp,
  Briefcase,
  Shield,
  Check,
  ArrowRight,
  ChevronRight
} from 'lucide-react';
import { useToast } from '../components/shared/ToastProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PersonaManagement = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPersonas();
  }, []);

  const fetchPersonas = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/personas`);
      if (response.ok) {
        const data = await response.json();
        setPersonas(data.personas || []);
      } else {
        throw new Error('Failed to fetch personas');
      }
    } catch (error) {
      console.error('Error fetching personas:', error);
      showToast('Failed to load personas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getPersonaRoute = (role) => {
    const routes = {
      'Recruiter': '/recruiter-riley',
      'Lead': '/lead-lena',
      'Sales': '/sales-sam',
      'HR': '/hr-hank',
      'Admin': '/admin-andrew',
      'Lead Sales': '/lead-sales-sarah'
    };
    return routes[role] || '/';
  };

  const getPersonaEmoji = (role) => {
    const emojis = {
      'Recruiter': '👤',
      'Lead': '👑',
      'Sales': '📈',
      'HR': '💼',
      'Admin': '🛡️',
      'Lead Sales': '📊'
    };
    return emojis[role] || '👤';
  };

  const getPersonaGradient = (color) => {
    const gradients = {
      '#10B981': 'from-green-400 to-green-600',
      '#8B5CF6': 'from-purple-400 to-purple-600',
      '#F59E0B': 'from-orange-400 to-orange-600',
      '#3B82F6': 'from-blue-400 to-blue-600',
      '#9333EA': 'from-purple-500 to-purple-700',
      '#F97316': 'from-orange-500 to-orange-700'
    };
    return gradients[color] || 'from-gray-400 to-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading personas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Welcome to Juno Talent Assist
        </h1>
        <p className="text-xl text-gray-600 mb-2">
          Who are you today?
        </p>
        <p className="text-sm text-gray-500">
          Select your role to access your personalized dashboard
        </p>
      </div>

      {/* Persona Selection Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">{/* Changed to 3 columns for better 6-persona layout */}
        {personas.map((persona) => {
          const emoji = getPersonaEmoji(persona.role);
          const gradient = getPersonaGradient(persona.color);
          const route = getPersonaRoute(persona.role);

          return (
            <button
              key={persona.id}
              onClick={() => navigate(route)}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border-2 border-gray-100 hover:border-transparent overflow-hidden"
            >
              {/* Background Gradient on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
              
              {/* Content */}
              <div className="relative z-10">
                {/* Emoji/Icon */}
                <div className="mb-6">
                  <div className="text-7xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                    {emoji}
                  </div>
                </div>

                {/* Persona Name */}
                <h3 className="text-2xl font-bold mb-2 group-hover:text-white transition-colors">
                  {persona.name}
                </h3>

                {/* Role */}
                <p className="text-sm font-medium text-gray-600 group-hover:text-white group-hover:text-opacity-90 transition-colors mb-4">
                  {persona.role}
                </p>

                {/* Description */}
                <p className="text-sm text-gray-500 group-hover:text-white group-hover:text-opacity-80 transition-colors mb-6 line-clamp-2">
                  {persona.description}
                </p>

                {/* Arrow Icon */}
                <div className="flex items-center justify-center">
                  <div className="flex items-center gap-2 text-sm font-semibold group-hover:text-white transition-colors">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity">Enter Dashboard</span>
                    <ChevronRight className="h-5 w-5 transform group-hover:translate-x-1 transition-transform" style={{ color: persona.color }} />
                  </div>
                </div>

                {/* Status Badge */}
                {persona.is_active && (
                  <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium group-hover:bg-white group-hover:bg-opacity-20 group-hover:text-white transition-colors">
                      <Check className="h-3 w-3" />
                      Active
                    </span>
                  </div>
                )}
              </div>

              {/* Hover Border Effect */}
              <div className={`absolute inset-0 rounded-2xl border-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300`} style={{ borderColor: persona.color }}></div>
            </button>
          );
        })}
      </div>

      {/* Info Section */}
      <div className="text-center max-w-2xl mx-auto mt-12 p-6 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Shield className="h-5 w-5 text-blue-600" />
          <p className="font-semibold text-blue-900">Authentication Coming Soon</p>
        </div>
        <p className="text-sm text-blue-700">
          Once authentication is fully implemented, you'll be automatically directed to your assigned dashboard based on your login credentials. For now, select any persona to explore their workspace.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-6xl mx-auto mt-8">{/* 6 columns for 6 personas */}
        {personas.map((persona) => (
          <div key={persona.id} className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-2xl mb-2">{getPersonaEmoji(persona.role)}</p>
            <p className="text-xs font-medium text-gray-600">{persona.role}</p>
            <p className="text-xs text-gray-500 mt-1 line-clamp-1">{persona.permissions.length} permissions</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PersonaManagement;
